import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { IssueList } from '../components/IssueList';
import { CreateIssueModal } from '../components/CreateIssueModal';
import type { Issue, Page } from '../types';

/** Все задачи трекера, поверх очередей: поиск и фильтры, когда очередь заранее не известна. */
export function IssuesPage() {
  const { tenantId = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const [text, setText] = useState('');
  const [queueId, setQueueId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [creating, setCreating] = useState(false);

  const criteria = {
    text: text || undefined,
    queueIds: queueId ? [queueId] : undefined,
    statusIds: statusId ? [statusId] : undefined,
    resolved: onlyOpen ? false : undefined,
  };

  const issues = useQuery({
    queryKey: ['issues', tenantId, criteria],
    queryFn: () =>
      api.post<Page<Issue>>(`/tenants/${tenantId}/issues/search?size=50&sort=created,desc`, criteria),
    enabled: catalog.queues.length > 0,
  });

  // Без очереди задачу завести невозможно, поэтому пустой трекер отправляем в очереди, а не
  // показываем недоступную кнопку.
  if (!catalog.isLoading && catalog.queues.length === 0) {
    return (
      <>
        <div className="page-head">
          <h1>Все задачи</h1>
        </div>
        <div className="card state">
          <div className="state-title">Сначала нужна очередь</div>
          Задача заводится внутри очереди — она задаёт ключ задач и правила работы.
          <div className="state-actions">
            <Link className="btn btn-primary" to={`/t/${tenantId}/queues`}>
              Перейти к очередям
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Все задачи</h1>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          Новая задача
        </button>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Поиск по ключу, заголовку, описанию"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <select className="input" value={queueId} onChange={(event) => setQueueId(event.target.value)}>
          <option value="">Все очереди</option>
          {catalog.queues.map((queue) => (
            <option key={queue.id} value={queue.id}>
              {queue.key} — {queue.name}
            </option>
          ))}
        </select>
        <select className="input" value={statusId} onChange={(event) => setStatusId(event.target.value)}>
          <option value="">Все статусы</option>
          {catalog.statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.name}
            </option>
          ))}
        </select>
        <label>
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(event) => setOnlyOpen(event.target.checked)}
          />
          только незакрытые
        </label>
        <div className="spacer" />
        <span className="muted small">
          {issues.data ? `Найдено: ${issues.data.totalElements}` : ''}
        </span>
      </div>

      {issues.isLoading && <div className="state">Загрузка…</div>}
      {issues.isError && <div className="state">{(issues.error as ApiError).message}</div>}
      {issues.data && issues.data.content.length === 0 && (
        <div className="card state">Ничего не найдено</div>
      )}
      {issues.data && issues.data.content.length > 0 && (
        <IssueList
          tenantId={tenantId}
          issues={issues.data.content}
          statusById={catalog.statusById}
        />
      )}

      {creating && (
        <CreateIssueModal
          tenantId={tenantId}
          queues={catalog.queues}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}